import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

export type StatusSection = "wip" | "staged" | "committed";

type StatusNavigationProps = {
  activeSection: StatusSection;
  onSectionChange: (section: StatusSection) => void;
  counts: Record<StatusSection, number>;
};

const Panel = styled(Paper)(({ theme }) => ({
  flex: "0 0 300px",
  display: "flex",
  flexDirection: "column",
  height: "fit-content",
  maxHeight: "calc(100vh - 200px)",
  padding: theme.spacing(2.5),
  backgroundColor: "#f8f8f8",
  borderRadius: "4px",
  boxSizing: "border-box",
  [theme.breakpoints.down("sm")]: {
    flexBasis: "100%",
  },
}));

const NavigationRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(1.2, 1.5),
  marginBottom: theme.spacing(0.5),
  cursor: "pointer",
  backgroundColor: isActive ? "#e0e0e0" : "transparent",
  color: isActive ? "#000" : "#555",
  borderRadius: "4px",
  transition: "background-color 0.2s ease",
  userSelect: "none",
  "&:hover": {
    backgroundColor: isActive ? "#e0e0e0" : "#ececec",
  },
  "&:focus-visible": {
    outline: "2px solid #1976d2",
    outlineOffset: "2px",
  },
}));

const SECTIONS: { section: StatusSection; label: string, description: string }[] = [
  {
    section: "wip",
    label: "WIP Conditions",
    description: "Conditions with no cells. Specification changes can be made at any time.",
  },
  {
    section: "staged",
    label: "Staged Conditions",
    description: "Conditions with cells created. Specification changes can be made at any time."
  },
  {
    section: "committed",
    label: "Committed+ Conditions",
    description: "Conditions with at least 1 cell at or past the \"Committed\" status. Specification changes can still be made, but they may result in regeneration of work order tickets."
  },
];

const StatusNavigation = ({
  activeSection,
  onSectionChange,
  counts,
}: StatusNavigationProps) => (
  <Panel elevation={0}>
    {SECTIONS.map(({ section, label, description }) => (
      <Tooltip
        title={description}
        placement="right"
        arrow
        enterDelay={500}
      >
        <NavigationRow
          key={section}
          isActive={activeSection === section}
          role="button"
          tabIndex={0}
          aria-current={activeSection === section ? "page" : undefined}
          onClick={() => onSectionChange(section)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSectionChange(section);
            }
          }}
        >
          <Typography variant="body2">{label}</Typography>
          <Typography variant="body2" color="text.secondary">
            {counts[section]}
          </Typography>
        </NavigationRow>
      </Tooltip>
    ))}
  </Panel>
);

export default StatusNavigation;
